/**
 * Das Formular der Anlageneinstellungen als Rechnung (HM-162).
 *
 * Seit dem überarbeiteten Entwurf (`ui_kits/dashboard/SettingsScreen.jsx`) gibt
 * es **ein** Speichern für Stammdaten und Anschrift zusammen, in einer Leiste,
 * die erst erscheint, wenn sich etwas geändert hat. Dafür braucht die Seite drei
 * Antworten, und sie stehen hier, ohne React und geprüft: Was steht im Formular,
 * wenn man es öffnet (`siteForm`)? Hat sich etwas geändert (`formDirty`)? Und
 * was geht hinaus — oder warum nicht (`formErrors`, `sitePatch`)?
 *
 * **Eine unlesbare Zahl wird nie still zu `null`.** Das löschte den gespeicherten
 * Wert, während der Nutzer glaubt, einen neuen zu setzen (Regel 9). Solange ein
 * Feld einen Fehler trägt, gibt es keinen Patch.
 */
import type { ApiComponents } from "@hausmeter/sdk";

type Site = ApiComponents["schemas"]["Site"];
type UpdateSiteRequest = ApiComponents["schemas"]["UpdateSiteRequest"];

export interface SiteForm {
  readonly name: string;
  readonly kind: Site["kind"];
  readonly timezone: string;
  readonly livingArea: string;
  readonly household: string;
  readonly street: string;
  readonly houseNumber: string;
  readonly addition: string;
  readonly postalCode: string;
  readonly city: string;
  readonly country: string;
  readonly latitude: string;
  readonly longitude: string;
}

export type SiteFormField = keyof SiteForm;
export type SiteFormErrors = Partial<Record<SiteFormField, string>>;

export function siteForm(site: Site): SiteForm {
  return {
    name: site.name,
    kind: site.kind,
    timezone: site.timezone,
    livingArea: numberField(site.livingAreaSqm),
    household: numberField(site.householdSize),
    street: site.address?.street ?? "",
    houseNumber: site.address?.houseNumber ?? "",
    addition: site.address?.addition ?? "",
    postalCode: site.address?.postalCode ?? "",
    city: site.address?.city ?? "",
    country: site.address?.country ?? "",
    latitude: numberField(site.latitude),
    longitude: numberField(site.longitude),
  };
}

export function formDirty(form: SiteForm, saved: SiteForm): boolean {
  return (Object.keys(form) as SiteFormField[]).some((key) => form[key] !== saved[key]);
}

/** Was an den Feldern nicht stimmt — am Feld gezeigt, nicht über der Seite. */
export function formErrors(form: SiteForm): SiteFormErrors {
  const errors: SiteFormErrors = {};
  if (form.name.trim().length === 0) errors.name = "Bitte einen Namen eingeben.";
  const area = measureError(form.livingArea, 100_000, "Quadratmeter");
  if (area !== undefined) errors.livingArea = area;
  const household = countError(form.household, 100);
  if (household !== undefined) errors.household = household;
  const latitude = coordinateError(form.latitude, 90);
  if (latitude !== undefined) errors.latitude = latitude;
  const longitude = coordinateError(form.longitude, 180);
  if (longitude !== undefined) errors.longitude = longitude;
  return errors;
}

/**
 * Der Patch für `PATCH /sites/{id}` — oder `null`, solange ein Feld einen Fehler
 * trägt. Leer heißt `null` und nicht `""` (`UpdateSiteRequestSchema`: `null`
 * entfernt). **Sind alle Teile der Anschrift leer, wird sie gelöscht** und nicht
 * als Objekt aus lauter Nullen geschrieben — im Umschalter tritt dann die
 * Zeitzone an ihre Stelle.
 */
export function sitePatch(form: SiteForm): UpdateSiteRequest | null {
  if (Object.keys(formErrors(form)).length > 0) return null;
  const address = {
    street: trimmedOrNull(form.street),
    houseNumber: trimmedOrNull(form.houseNumber),
    addition: trimmedOrNull(form.addition),
    postalCode: trimmedOrNull(form.postalCode),
    city: trimmedOrNull(form.city),
    country: trimmedOrNull(form.country)?.toUpperCase() ?? null,
  };
  return {
    name: form.name.trim(),
    kind: form.kind,
    timezone: form.timezone,
    livingAreaSqm: parsed(form.livingArea),
    householdSize: parsed(form.household),
    address: Object.values(address).every((value) => value === null) ? null : address,
    latitude: parsed(form.latitude),
    longitude: parsed(form.longitude),
  };
}

/**
 * Die Zeitzonen zur Auswahl — die des Entwurfs. **Eine andere, die schon
 * gespeichert ist, bleibt wählbar** und steht vorn: Der Vertrag nimmt jede
 * IANA-Zone, und eine Auswahl ohne den gespeicherten Wert zeigte einen anderen an,
 * als gilt — beim nächsten Speichern schriebe sie ihn still um.
 */
export const TIME_ZONES: readonly { value: string; label: string }[] = [
  { value: "Europe/Berlin", label: "Deutschland · Europe/Berlin" },
  { value: "Europe/Vienna", label: "Österreich · Europe/Vienna" },
  { value: "Europe/Zurich", label: "Schweiz · Europe/Zurich" },
  { value: "Europe/Amsterdam", label: "Niederlande · Europe/Amsterdam" },
  { value: "Europe/Luxembourg", label: "Luxemburg · Europe/Luxembourg" },
];

/** Die Länder zur Auswahl — dieselbe Regel: ein gespeichertes anderes bleibt. „Keine Angabe" ist leer. */
export const COUNTRIES: readonly { value: string; label: string }[] = [
  { value: "DE", label: "Deutschland" },
  { value: "AT", label: "Österreich" },
  { value: "CH", label: "Schweiz" },
  { value: "NL", label: "Niederlande" },
  { value: "LU", label: "Luxemburg" },
];

export function optionsWith(
  options: readonly { value: string; label: string }[],
  current: string,
): readonly { value: string; label: string }[] {
  if (current === "" || options.some((option) => option.value === current)) return options;
  return [{ value: current, label: current }, ...options];
}

/** „52.1416° N · 8.5583° O" — mit Punkt: Wer Koordinaten kopiert, gibt sie anderswo wieder ein. */
export function coordinateSummary(latitude: string, longitude: string): string | null {
  const lat = parseNumber(latitude);
  const lon = parseNumber(longitude);
  if (lat === null || lon === null || Number.isNaN(lat) || Number.isNaN(lon)) return null;
  return `${Math.abs(lat).toFixed(4)}° ${lat >= 0 ? "N" : "S"} · ${Math.abs(lon).toFixed(4)}° ${lon >= 0 ? "O" : "W"}`;
}

function numberField(value: number | null): string {
  return value === null ? "" : String(value);
}

function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** Das Dezimalkomma wird angenommen: Eine deutsche Tastatur legt es nahe. */
function parseNumber(value: string): number | null {
  const trimmed = value.trim().replace(",", ".");
  return trimmed.length === 0 ? null : Number(trimmed);
}

function parsed(value: string): number | null {
  const number = parseNumber(value);
  return number === null || Number.isNaN(number) ? null : number;
}

function measureError(value: string, max: number, unit: string): string | undefined {
  const number = parseNumber(value);
  if (number === null) return undefined;
  if (Number.isNaN(number)) return "Bitte eine Zahl eingeben, etwa 184.";
  if (number <= 0) return "Bitte eine Zahl größer als null eingeben.";
  return number > max ? `Mehr als ${max.toLocaleString("de-DE")} ${unit} sind wohl ein Vertipper.` : undefined;
}

function countError(value: string, max: number): string | undefined {
  const number = parseNumber(value);
  if (number === null) return undefined;
  if (Number.isNaN(number)) return "Bitte eine Zahl eingeben, etwa 4.";
  if (!Number.isInteger(number)) return "Bitte eine ganze Zahl eingeben — halbe Personen gibt es nicht.";
  if (number < 1) return "Mindestens eine Person.";
  return number > max ? `Höchstens ${max}.` : undefined;
}

function coordinateError(value: string, limit: number): string | undefined {
  const number = parseNumber(value);
  if (number === null) return undefined;
  if (Number.isNaN(number)) return "Bitte eine Zahl eingeben, etwa 50.9375.";
  return Math.abs(number) > limit ? `Erlaubt sind −${limit} bis ${limit}.` : undefined;
}
